/**
 * useLiveAvatarSession — the Anam live-video session lifecycle, ported from
 * the web app (HypeTunes_Web_2026/src/hooks/useLiveAvatarSession.js) so the
 * World can host the SAME live call the Studio page runs: same endpoints,
 * same per-minute prepaid billing + refund, same custom brain.
 *
 * Flow: prefetch status (cost + cap + eligibility) → start() charges the
 * prepaid pass via POST /api/anam/session-token → open the WebRTC stream into
 * `videoElementId` → hard countdown → report end (refunds unused minutes).
 *
 * BRAIN: Anam's own LLM is disabled (CUSTOMER_CLIENT_V1). Anam does
 * face/voice/STT only; MESSAGE_HISTORY_UPDATED hands us the transcript, we
 * POST it to /api/anam/session/:id/turn (the same ChatService/Claude pipeline
 * and thread as text chat) and speak the reply with client.talk().
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE, AUTH_TOKEN } from '../config';

export const fmtClock = (s) => `${Math.floor(s / 60)}:${String(Math.max(0, s % 60)).padStart(2, '0')}`;

export default function useLiveAvatarSession({ avatarId, videoElementId, token = AUTH_TOKEN }) {
  const [phase, setPhase] = useState('confirm'); // confirm | connecting | live | ended | error
  const [message, setMessage] = useState('');
  const [costPerMinute, setCostPerMinute] = useState(null);
  const [minutes, setMinutes] = useState(3);
  const [maxSeconds, setMaxSeconds] = useState(300);
  const [secondsLeft, setSecondsLeft] = useState(300);
  const [status, setStatus] = useState(null); // { allowed, ready, canStart, requiresPlan, ... }

  const clientRef = useRef(null);
  const sessionIdRef = useRef(null);
  const reportedEndRef = useRef(false);
  const tickRef = useRef(null);
  const turnSeqRef = useRef(0);      // drops stale in-flight turn replies
  const lastUserTurnRef = useRef(''); // dedupes repeated history events

  const cleanup = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    try { clientRef.current?.stopStreaming?.(); } catch { /* noop */ }
    clientRef.current = null;
  }, []);

  // Prefetch cost/length/eligibility — the gate the dock uses to decide
  // whether to offer the live button at all.
  useEffect(() => {
    let cancelled = false;
    if (!token || avatarId == null) { setStatus(null); return undefined; }
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/anam/avatar/${avatarId}/status`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        setStatus(d || null);
        if (d.costPerMinute != null) setCostPerMinute(d.costPerMinute);
        if (d.maxSeconds) { setMaxSeconds(d.maxSeconds); setSecondsLeft(d.maxSeconds); }
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; cleanup(); };
  }, [avatarId, token, cleanup]);

  const report = useCallback(async (body) => {
    const sid = sessionIdRef.current;
    if (!sid) return null;
    try {
      const r = await fetch(`${API_BASE}/api/anam/session/${sid}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      return await r.json().catch(() => null);
    } catch { return null; /* best effort */ }
  }, [token]);

  const endCall = useCallback(async (reason = 'ended') => {
    cleanup();
    let refunded = 0;
    if (!reportedEndRef.current) {
      reportedEndRef.current = true;
      const d = await report({ event: 'ended' });
      refunded = d?.refunded || 0;
    }
    setPhase('ended');
    const base = reason === 'timeup' ? "That's time! Your session ended." : 'Session ended.';
    setMessage(refunded > 0 ? `${base} ${refunded} unused credit${refunded > 1 ? 's' : ''} refunded.` : base);
  }, [cleanup, report]);

  const start = useCallback(async () => {
    setPhase('connecting'); setMessage('Starting your session…');
    reportedEndRef.current = false;
    try {
      // Load the SDK BEFORE charging: with the custom brain, the
      // MESSAGE_HISTORY_UPDATED loop is load-bearing — if the engine can't
      // supply it, fail here instead of after the prepaid charge.
      const mod = await import(/* @vite-ignore */ 'https://esm.sh/@anam-ai/js-sdk@latest');
      const createClient = mod.createClient || mod.default?.createClient;
      const AnamEvent = mod.AnamEvent || mod.default?.AnamEvent || {};
      if (!createClient || !AnamEvent.MESSAGE_HISTORY_UPDATED) throw new Error('Live avatar engine failed to load.');

      const r = await fetch(`${API_BASE}/api/anam/session-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ avatarId, minutes }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.success) {
        if (r.status === 401) throw new Error('Your login token expired — grab a fresh one and reload with ?token=…');
        if (r.status === 402) throw new Error(`Not enough credits — this session needs ${data.required ?? (costPerMinute != null ? costPerMinute * minutes : '')} credits.`);
        if (r.status === 403) throw new Error('Live video chat is a Creator/Studio feature.');
        if (r.status === 429) throw new Error("You've hit today's live-chat limit. Try again tomorrow.");
        if (r.status === 503) throw new Error(data.error || 'Live chat is busy right now — try again in a moment.');
        throw new Error(data.error || 'Could not start the session.');
      }
      sessionIdRef.current = data.sessionId;
      if (data.maxSeconds) { setMaxSeconds(data.maxSeconds); setSecondsLeft(data.maxSeconds); }

      setMessage('Connecting…');

      const client = createClient(data.sessionToken);
      clientRef.current = client;

      // Our brain: send a turn to the server (ChatService/Claude — the same
      // pipeline as text chat) and speak the reply. Stale responses (the user
      // spoke again, or the call ended) are dropped via the seq guard.
      const runTurn = async (body) => {
        const seq = ++turnSeqRef.current;
        try {
          const tr = await fetch(`${API_BASE}/api/anam/session/${sessionIdRef.current}/turn`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
          });
          const td = await tr.json().catch(() => ({}));
          if (!tr.ok || !td.reply) throw new Error(td.error || `turn failed (${tr.status})`);
          if (seq !== turnSeqRef.current || clientRef.current !== client) return;
          client.talk(td.reply);
        } catch (err) {
          console.error('[LiveAvatar] turn failed:', err);
          if (seq !== turnSeqRef.current || clientRef.current !== client) return;
          if (body.greeting) client.talk(`Hey! It's so good to see you.`);
          else client.talk(`Sorry — I missed that. Say it again?`);
        }
      };

      // Report the Anam session id (powers the server stop-backstop) + handle end.
      try {
        if (AnamEvent.SESSION_READY) client.addListener(AnamEvent.SESSION_READY, (sid) => {
          report({ anamSessionId: sid, event: 'started' });
          runTurn({ greeting: true }); // open the call in-persona
        });
        if (AnamEvent.SESSION_ENDED) client.addListener(AnamEvent.SESSION_ENDED, () => { endCall('ended'); });
        // The custom-LLM loop: fires when the user finishes speaking, with the
        // transcribed conversation history (last entry = the user's turn).
        if (AnamEvent.MESSAGE_HISTORY_UPDATED) client.addListener(AnamEvent.MESSAGE_HISTORY_UPDATED, (messages) => {
          const last = messages?.[messages.length - 1];
          if (!last || last.role !== 'user') return;
          const transcript = String(last.content || '').trim();
          const key = `${messages.length}:${transcript}`;
          if (!transcript || key === lastUserTurnRef.current) return;
          lastUserTurnRef.current = key;
          runTurn({ transcript });
        });
      } catch { /* listeners optional */ }

      await client.streamToVideoElement(videoElementId);
      setPhase('live');
      setMessage('');
      // Hard countdown = the cutoff.
      const t0 = data.maxSeconds || maxSeconds;
      setSecondsLeft(t0);
      tickRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) { clearInterval(tickRef.current); tickRef.current = null; endCall('timeup'); return 0; }
          return s - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('[LiveAvatar] start failed:', err);
      cleanup();
      setPhase('error');
      setMessage(err.message || 'Could not start the session.');
    }
  }, [avatarId, token, minutes, costPerMinute, maxSeconds, videoElementId, report, endCall, cleanup]);

  const reset = useCallback(() => {
    cleanup();
    sessionIdRef.current = null;
    reportedEndRef.current = false;
    setPhase('confirm');
    setMessage('');
    setSecondsLeft(maxSeconds);
  }, [cleanup, maxSeconds]);

  return {
    phase, message, status,
    costPerMinute, minutes, setMinutes, maxSeconds, secondsLeft,
    live: phase === 'live',
    start, endCall, reset,
  };
}
